import CheckIcon from "@mui/icons-material/Check";
import LockIcon from "@mui/icons-material/Lock";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  Box,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  createColumnHelper,
  getCoreRowModel,
  type PaginationState,
  useReactTable,
} from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useActorsQuery } from "Actors/Api/Queries/useActorsQuery";
import {
  type ActorListItemDto,
  ActorStatus,
  type GenderType,
  type Industry,
  type LanguageCodes,
  type ProjectActorDto,
  type VoiceLlm,
} from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { UserAvatar } from "Shared/Avatar/UserAvatar";
import { BlTanstackTable } from "Shared/DataTable/BlTanstackTable";
import {
  languageFilterOptions,
  priceFilterOptions,
  sexFilterOptions,
} from "Shared/DataTable/filterOptions";
import { BlModal } from "Shared/Dialogs/BlModal";
import { BlDropdown } from "Shared/Inputs/BlDropdown";

interface ProjectActorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (actorIds: string[]) => void;
  currentActors?: ProjectActorDto[];
  currentActorIds?: string[];
  exclusiveActorIds?: string[];
  blockedActorIds?: string[];
  maxActors?: number;
  priceCategoryID?: string;
  voiceLlms?: VoiceLlm[];
  industry?: Industry;
}

const columnHelper = createColumnHelper<ActorListItemDto>();

/** Map assigned ProjectActorDto to ActorListItemDto shape for unified table rendering. */
const toActorListItem = (actor: ProjectActorDto): ActorListItemDto => ({
  actorID: actor.actorID,
  name: `${actor.firstName} ${actor.lastName}`,
  profilePictureUrl: actor.profilePictureUrl,
  language: null as unknown as LanguageCodes,
  gender: null,
  age: null,
  price: null,
  status: ActorStatus.Active,
  czechPreviewFileID: null,
  englishPreviewFileID: null,
  deutschPreviewFileID: null,
});

export const ProjectActorsModal = ({
  isOpen,
  onClose,
  onConfirm,
  currentActors = [],
  currentActorIds = [],
  exclusiveActorIds = [],
  blockedActorIds = [],
  maxActors,
  priceCategoryID,
  voiceLlms,
  industry,
}: ProjectActorsModalProps): React.ReactNode => {
  const { t } = useResource();

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [selectedSex, setSelectedSex] = useState<string>("");
  const [selectedPrice, setSelectedPrice] = useState<string>("");
  const [selectedActorIds, setSelectedActorIds] = useState<Set<string>>(
    () => new Set(currentActorIds),
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedActorIds(new Set(currentActorIds));
    }
  }, [isOpen, currentActorIds]);

  const exclusiveActorIdSet = useMemo(
    () => new Set(exclusiveActorIds),
    [exclusiveActorIds],
  );

  const blockedActorIdSet = useMemo(
    () => new Set(blockedActorIds),
    [blockedActorIds],
  );

  const currentActorIdSet = useMemo(
    () => new Set(currentActorIds),
    [currentActorIds],
  );

  const { data, isLoading } = useActorsQuery({
    offset: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
    searchQuery: searchQuery || null,
    languages: selectedLanguage
      ? [selectedLanguage as LanguageCodes]
      : undefined,
    genders: selectedSex ? [selectedSex as GenderType] : undefined,
    priceCategoryID: priceCategoryID,
    onlyOpenToCollaboration: true,
    onlyWithActiveVoice: true,
    actorStatus: [ActorStatus.Active],
    excludeNotAllowed: !!priceCategoryID,
    voiceLlms: voiceLlms,
    industry: industry,
    includeActorIDs: currentActorIds.length > 0 ? currentActorIds : undefined,
  });

  // On first page: prepend assigned actors, filter duplicates from API results
  const assignedRows = useMemo(
    () =>
      pagination.pageIndex === 0 ? currentActors.map(toActorListItem) : [],
    [currentActors, pagination.pageIndex],
  );

  const apiRows = useMemo(
    () => (data?.items ?? []).filter((a) => !currentActorIdSet.has(a.actorID)),
    [data?.items, currentActorIdSet],
  );

  const allRows = useMemo(
    () => [...assignedRows, ...apiRows],
    [assignedRows, apiRows],
  );

  const toggleActorSelection = useCallback(
    (actorId: string) => {
      setSelectedActorIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(actorId)) {
          newSet.delete(actorId);
        } else {
          if (maxActors !== undefined && newSet.size >= maxActors) {
            newSet.clear();
          }
          newSet.add(actorId);
        }
        return newSet;
      });
    },
    [maxActors],
  );

  const handleConfirm = () => {
    onConfirm(Array.from(selectedActorIds));
    setSelectedActorIds(new Set());
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setSearchQuery("");
  };

  const handleClose = () => {
    setSelectedActorIds(new Set());
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setSearchQuery("");
    onClose();
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: t(Resources.Actors.Name),
        enableSorting: false,
        cell: (info) => {
          const actor = info.row.original;
          const nameParts = actor.name.split(" ");
          const firstName = nameParts[0] ?? "";
          const lastName = nameParts.slice(1).join(" ");
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <UserAvatar
                firstName={firstName}
                lastName={lastName}
                imageUrl={actor.profilePictureUrl}
                size={32}
              />
              <Box>{actor.name}</Box>
            </Box>
          );
        },
      }),
      columnHelper.display({
        id: "action",
        header: "",
        enableSorting: false,
        meta: { maxWidth: 120, justifySelf: "end" },
        cell: (info) => {
          const actorID = info.row.original.actorID;
          const isExclusive = exclusiveActorIdSet.has(actorID);
          const isBlocked = blockedActorIdSet.has(actorID);
          const isAdded = selectedActorIds.has(actorID);
          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                gap: 1,
              }}
            >
              {isExclusive ? (
                <Chip
                  label={t(Resources.Projects.ProjectDetail.ExclusiveContract)}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ fontSize: "11px", height: "22px" }}
                />
              ) : isBlocked ? (
                <Tooltip
                  title={t(
                    Resources.Projects.ProjectActorsModal
                      .CannotRemoveActorWithActiveJobs,
                  )}
                >
                  <IconButton
                    size="small"
                    disabled
                    aria-label={t(
                      Resources.Projects.ProjectActorsModal
                        .CannotRemoveActorWithActiveJobs,
                    )}
                  >
                    <LockIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <IconButton
                  size="small"
                  onClick={() => toggleActorSelection(actorID)}
                  sx={(theme) => ({
                    color: isAdded ? theme.palette.success.main : "inherit",
                  })}
                  aria-label="toggle actor"
                >
                  {isAdded ? <CheckIcon /> : <PersonAddIcon />}
                </IconButton>
              )}
            </Box>
          );
        },
      }),
    ],
    [
      t,
      selectedActorIds,
      exclusiveActorIdSet,
      blockedActorIdSet,
      toggleActorSelection,
    ],
  );

  const filteredOutCount = (data?.items ?? []).length - apiRows.length;
  const adjustedTotalCount = Math.max(
    0,
    (data?.totalCount ?? 0) - filteredOutCount + assignedRows.length,
  );

  const table = useReactTable({
    data: allRows,
    columns,
    getRowId: (row) => row.actorID,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    rowCount: adjustedTotalCount,
    state: { pagination },
    onPaginationChange: setPagination,
  });

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.Projects.ProjectActorsModal.Title)}
      onCancel={handleClose}
      onConfirm={handleConfirm}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.Projects.ProjectActorsModal.Approve)}
      maxWidth="1200px"
    >
      <Box>
        <Stack
          direction="row"
          spacing={Spacing.S}
          sx={{ marginBottom: Spacing.M }}
        >
          <Box sx={{ flex: 1 }}>
            <TextField
              fullWidth
              placeholder={t(Resources.Actors.ActorName)}
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: (theme) => theme.colors.backgroundTile,
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.colors.backgroundTile,
                },
                "& .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: (theme) => theme.colors.backgroundTile,
                },
                "& .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: (theme) => theme.colors.backgroundTile,
                  },
              }}
            />
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <BlDropdown
              name="language-filter"
              options={languageFilterOptions()}
              value={selectedLanguage}
              onChange={(value) => {
                setSelectedLanguage(value as string);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              placeholder={t(Resources.Actors.NativeLanguage)}
            />
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <BlDropdown
              name="sex-filter"
              options={sexFilterOptions()}
              value={selectedSex}
              onChange={(value) => {
                setSelectedSex(value as string);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              placeholder={t(Resources.Actors.Sex)}
            />
          </Box>
          <Box sx={{ minWidth: 200 }}>
            <BlDropdown
              name="price-filter"
              options={priceFilterOptions()}
              value={selectedPrice}
              onChange={(value) => {
                setSelectedPrice(value as string);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              placeholder={t(Resources.Actors.PricePerModule)}
            />
          </Box>
        </Stack>
        <BlTanstackTable
          table={table}
          isLoading={isLoading}
          totalCount={adjustedTotalCount}
        />
      </Box>
    </BlModal>
  );
};
